// Messaging Monitor — poll orchestrator
//
// Coordinates Slack + Teams polling cycles, snapshot generation, anomaly
// detection, and bot action queuing. Registers and communicates with the
// messaging service worker for background keep-alive.
//
// Lifecycle:
//   1. Call start() when the user enables monitoring.
//   2. The monitor polls immediately, then on a configurable interval.
//   3. A service worker is registered to keep polling alive when the tab is
//      backgrounded. The SW posts SwMessages back to the page.
//   4. Call stop() to halt polling and unregister the SW.

import { slackService } from './slack-service';
import { teamsService } from './teams-service';
import { botService } from './messaging-bot';
import {
  getSlackSettings,
  getTeamsSettings,
  saveSlackSettings,
  saveTeamsSettings,
  saveMessages,
  saveSnapshot,
  getRecentSnapshots,
  getMessagesByDay,
  loadCursors,
  saveCursors,
} from './messaging-storage';
import { toLocalDateStr } from '../lib/utils';
import * as historyStore from './analysis-history-store';
import type {
  MessagingPlatform,
  MessagingSnapshot,
  SwMessage,
} from './messaging-types';

// ─── Config ───────────────────────────────────────────────────────────────────

/** How often to poll when the tab is in the foreground (ms) */
const FOREGROUND_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/** Path to the compiled service worker script */
const SW_SCRIPT = '/messaging-sw.js';

// ─── Listeners ────────────────────────────────────────────────────────────────

type MonitorEvent = 'poll_complete' | 'anomaly' | 'error';
type MonitorListener = (event: MonitorEvent, data: unknown) => void;

// ─── MessagingMonitor ─────────────────────────────────────────────────────────

export class MessagingMonitor {
  private static instance: MessagingMonitor;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private listeners: Set<MonitorListener> = new Set();
  private running = false;
  /** key → last alerted severity; prevents re-alerting same anomaly each poll */
  private alertedAnomalies = new Map<string, string>();
  /** Prevents two concurrent poll cycles from running simultaneously */
  private pollInProgress = false;

  private constructor() {}

  static getInstance(): MessagingMonitor {
    if (!MessagingMonitor.instance) MessagingMonitor.instance = new MessagingMonitor();
    return MessagingMonitor.instance;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  async start(platforms?: MessagingPlatform[]): Promise<void> {
    if (this.running) return;
    this.running = true;

    // Restore alerted anomalies from localStorage (synchronous — survives reload reliably)
    this.alertedAnomalies = this.loadAlertedAnomaliesSync();

    const activePlatforms = platforms ?? await this.detectActivePlatforms();
    if (activePlatforms.length === 0) {
      this.running = false;
      return;
    }

    // Initial poll
    await this.pollAll(activePlatforms);

    // Foreground interval
    this.pollTimer = setInterval(
      () => this.pollAll(activePlatforms),
      FOREGROUND_POLL_INTERVAL_MS,
    );

    // Register service worker for background keep-alive
    await this.registerServiceWorker(activePlatforms);
  }

  stop(): void {
    this.running = false;
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    this.unregisterServiceWorker();
  }

  get isRunning(): boolean { return this.running; }

  on(listener: MonitorListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: MonitorEvent, data: unknown): void {
    this.listeners.forEach(l => l(event, data));
  }

  // ── Alerted-anomaly persistence (localStorage — synchronous, reload-safe) ─

  private readonly LS_KEY = 'verigit-alerted-anomalies';

  private loadAlertedAnomaliesSync(): Map<string, string> {
    try {
      const raw = localStorage.getItem(this.LS_KEY);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw) as { date: string; entries: Record<string, string> };
      if (parsed.date !== toLocalDateStr()) return new Map(); // stale day — start fresh
      return new Map(Object.entries(parsed.entries ?? {}));
    } catch {
      return new Map();
    }
  }

  private saveAlertedAnomaliesSync(): void {
    try {
      localStorage.setItem(this.LS_KEY, JSON.stringify({
        date: toLocalDateStr(),
        entries: Object.fromEntries(this.alertedAnomalies),
      }));
    } catch { /* quota exceeded — non-fatal */ }
  }

  // ── Platform detection ────────────────────────────────────────────────────

  private async detectActivePlatforms(): Promise<MessagingPlatform[]> {
    const platforms: MessagingPlatform[] = [];
    const slack = await getSlackSettings();
    if (slack?.enabled && slack.accessToken) platforms.push('slack');
    const teams = await getTeamsSettings();
    if (teams?.enabled && teams.accessToken) platforms.push('teams');
    return platforms;
  }

  // ── Poll cycle ────────────────────────────────────────────────────────────

  async pollAll(platforms: MessagingPlatform[]): Promise<void> {
    if (this.pollInProgress) return;
    this.pollInProgress = true;
    try {
      for (const platform of platforms) {
        try {
          if (platform === 'slack') await this.pollSlack();
          if (platform === 'teams') await this.pollTeams();
        } catch (err) {
          console.error(`[MessagingMonitor] ${platform} poll failed:`, err);
          this.emit('error', { platform, error: String(err) });
        }
      }
    } finally {
      this.pollInProgress = false;
    }
  }

  private async pollSlack(): Promise<void> {
    const settings = await getSlackSettings();
    if (!settings?.enabled || !settings.accessToken) return;

    // Build member name map
    const members = await slackService.listMembers(settings.accessToken);
    const memberMap = new Map(members.map(m => [m.id, m.displayName]));

    // Load cursors
    const cursors = await loadCursors('slack');

    const { allMessages, updatedCursors, channelNameMap } = await slackService.pollAll(settings, cursors, memberMap);

    if (allMessages.length > 0) {
      await saveMessages(allMessages);
    }
    await saveCursors('slack', updatedCursors);

    // Update lastPolledAt
    await saveSlackSettings({ ...settings, lastPolledAt: new Date().toISOString() });

    // Build snapshot from ALL of today's messages in IndexedDB (not just newly-polled batch)
    const today = toLocalDateStr();
    const todaysMessages = await getMessagesByDay('slack', today);
    const baseline = (await getRecentSnapshots('slack', 7)).find(s => s.date !== today) ?? null;
    const partialSnapshot = slackService.buildSnapshot(todaysMessages, today, memberMap, channelNameMap);
    const anomalies = slackService.detectAnomalies(partialSnapshot, baseline, todaysMessages);
    const snapshot: MessagingSnapshot = { ...partialSnapshot, anomalies };

    await saveSnapshot(snapshot);

    // Queue alerts for high-severity anomalies — deduplicate across polls
    const alertChannelId = settings.monitoredChannelIds[0];
    if (alertChannelId) {
      const toAlert = anomalies.filter(a => {
        if (a.severity !== 'high' && a.severity !== 'medium') return false;
        const key = `${a.type}:${a.channelId ?? ''}:${a.memberId ?? ''}:${a.messageId ?? ''}`;
        const prev = this.alertedAnomalies.get(key);
        const severityRank = (s: string) => s === 'high' ? 2 : s === 'medium' ? 1 : 0;
        if (prev && severityRank(a.severity) <= severityRank(prev)) return false;
        this.alertedAnomalies.set(key, a.severity);
        this.saveAlertedAnomaliesSync();
        return true;
      });
      for (const a of toAlert) {
        await botService.queueAnomalyAlert('slack', a, alertChannelId);
      }
      if (toAlert.length > 0) await botService.flushQueue('slack');
    }

    // Check for bot mentions and respond
    for (const msg of allMessages) {
      if (!msg.text.toLowerCase().includes('@verigit')) continue;
      const response = botService.buildQueryResponse(msg.text, snapshot);
      if (response) {
        await botService.queueQueryResponse('slack', response, msg.channelId);
        await botService.flushQueue('slack');
      }
    }

    // Save to Reports & Analytics history store (fire-and-forget)
    historyStore.saveSnapshot(
      {
        id: historyStore.generateSnapshotId(),
        mode: 'slack' as const,
        title: `Slack — ${today}`,
        timestamp: new Date().toISOString(),
        durationMs: 0,
        success: true,
        summary: {
          commitCount: 0,
          eventCount: snapshot.memberStats.reduce((s, m) => s + m.messageCount, 0),
          authorCount: snapshot.memberStats.length,
          extra: {
            messages: snapshot.memberStats.reduce((s, m) => s + m.messageCount, 0),
            reactions: snapshot.memberStats.reduce((s, m) => s + m.totalReactions, 0),
            anomalies: anomalies.length,
          },
        },
      },
      snapshot,
    ).then(() => window.dispatchEvent(new CustomEvent('verigit:snapshot-saved', { detail: { mode: 'slack' } }))).catch(() => {});

    this.emit('poll_complete', { platform: 'slack', newMessageCount: allMessages.length, anomalies });
  }

  private async pollTeams(): Promise<void> {
    const settings = await getTeamsSettings();
    if (!settings?.enabled || !settings.accessToken) return;

    const teamIds = settings.monitoredTeamIds.length > 0
      ? settings.monitoredTeamIds
      : (await teamsService.listJoinedTeams(settings.accessToken)).map(t => t.id);

    // Build member name map from all teams
    const memberMap = new Map<string, string>();
    for (const teamId of teamIds) {
      try {
        const members = await teamsService.listTeamMembers(settings.accessToken, teamId);
        members.forEach(m => memberMap.set(m.id, m.displayName));
      } catch { /* skip teams we can't read */ }
    }

    const cursors = await loadCursors('teams');
    const { allMessages, updatedCursors } = await teamsService.pollAll(settings, cursors, memberMap);

    if (allMessages.length > 0) {
      await saveMessages(allMessages);
    }
    await saveCursors('teams', updatedCursors);

    await saveTeamsSettings({ ...settings, lastPolledAt: new Date().toISOString() });

    // Build snapshot from ALL of today's messages in IndexedDB (not just newly-polled batch)
    const today = toLocalDateStr();
    const todaysMessages = await getMessagesByDay('teams', today);
    const baseline = (await getRecentSnapshots('teams', 7)).find(s => s.date !== today) ?? null;
    const partialSnapshot = teamsService.buildSnapshot(todaysMessages, today);
    const anomalies = teamsService.detectAnomalies(partialSnapshot, baseline, todaysMessages);
    const snapshot: MessagingSnapshot = { ...partialSnapshot, anomalies };

    await saveSnapshot(snapshot);

    // Queue high-severity alerts to first monitored channel (if available)
    const firstChannel = snapshot.channelStats[0];
    if (firstChannel?.teamId && anomalies.some(a => a.severity === 'high')) {
      for (const a of anomalies.filter(a => a.severity === 'high')) {
        await botService.queueAnomalyAlert('teams', a, firstChannel.channelId, firstChannel.teamId);
      }
      await botService.flushQueue('teams');
    }

    // Check for bot mentions
    for (const msg of allMessages) {
      if (!msg.text.toLowerCase().includes('@verigit')) continue;
      if (!msg.teamId) continue;
      const response = botService.buildQueryResponse(msg.text, snapshot);
      if (response) {
        await botService.queueQueryResponse('teams', response, msg.channelId, msg.teamId);
        await botService.flushQueue('teams');
      }
    }

    // Save to Reports & Analytics history store (fire-and-forget)
    historyStore.saveSnapshot(
      {
        id: historyStore.generateSnapshotId(),
        mode: 'teams' as const,
        title: `Teams — ${today}`,
        timestamp: new Date().toISOString(),
        durationMs: 0,
        success: true,
        summary: {
          commitCount: 0,
          eventCount: snapshot.memberStats.reduce((s, m) => s + m.messageCount, 0),
          authorCount: snapshot.memberStats.length,
          extra: {
            messages: snapshot.memberStats.reduce((s, m) => s + m.messageCount, 0),
            reactions: snapshot.memberStats.reduce((s, m) => s + m.totalReactions, 0),
            anomalies: anomalies.length,
          },
        },
      },
      snapshot,
    ).then(() => window.dispatchEvent(new CustomEvent('verigit:snapshot-saved', { detail: { mode: 'teams' } }))).catch(() => {});

    this.emit('poll_complete', { platform: 'teams', newMessageCount: allMessages.length, anomalies });
  }

  // ── Daily summary ─────────────────────────────────────────────────────────

  /**
   * Post a daily summary for a platform. Typically called at 09:00 local time
   * from a scheduled check inside the polling loop or service worker.
   */
  async postDailySummary(platform: MessagingPlatform): Promise<void> {
    const today = new Date().toISOString().slice(0, 10);
    const [snapshot] = await getRecentSnapshots(platform, 1);
    if (!snapshot || snapshot.date !== today) return;

    if (platform === 'slack') {
      const settings = await getSlackSettings();
      if (!settings?.enabled) return;
      const targetChannel = settings.monitoredChannelIds[0];
      if (!targetChannel) return;
      await botService.queueDailySummary('slack', snapshot, targetChannel);
      await botService.flushQueue('slack');
    }

    if (platform === 'teams') {
      const settings = await getTeamsSettings();
      if (!settings?.enabled) return;
      const teams = await teamsService.listJoinedTeams(settings.accessToken);
      if (teams.length === 0) return;
      const channels = await teamsService.listChannels(settings.accessToken, teams[0].id, teams[0].name);
      if (channels.length === 0) return;
      await botService.queueDailySummary('teams', snapshot, channels[0].id, teams[0].id);
      await botService.flushQueue('teams');
    }
  }

  // ── Service Worker ────────────────────────────────────────────────────────

  private async registerServiceWorker(platforms: MessagingPlatform[]): Promise<void> {
    if (!('serviceWorker' in navigator)) return;

    try {
      const reg = await navigator.serviceWorker.register(SW_SCRIPT, { scope: '/' });
      this.swRegistration = reg;

      // Tell the SW which platforms to monitor
      const msg: SwMessage = { type: 'START_MONITORING', platforms };
      reg.active?.postMessage(msg);

      // Listen for messages from SW
      navigator.serviceWorker.addEventListener('message', this.handleSwMessage);
    } catch (err) {
      console.warn('[MessagingMonitor] Service worker registration failed:', err);
    }
  }

  private unregisterServiceWorker(): void {
    if (!this.swRegistration) return;
    navigator.serviceWorker.removeEventListener('message', this.handleSwMessage);
    const msg: SwMessage = { type: 'STOP_MONITORING' };
    this.swRegistration.active?.postMessage(msg);
    this.swRegistration = null;
  }

  private handleSwMessage = (event: MessageEvent): void => {
    const msg = event.data as SwMessage;
    if (!msg) return;

    switch (msg.type) {
      case 'POLL_COMPLETE':
        this.emit('poll_complete', { platform: msg.platform, newMessageCount: msg.newMessageCount });
        break;
      case 'ANOMALY_DETECTED':
        this.emit('anomaly', msg.anomaly);
        break;
    }
  };
}

export const messagingMonitor = MessagingMonitor.getInstance();
